import {
  Heading,
  HStack,
  Spinner,
  VStack,
  Select,
  Text,
  useToast,
  Button,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SidebarWithHeader, OrderDetails } from '../components';
import { useOrderContext } from '../context/order_context';
import { orderStatusList } from '../utils/constants';
import { FiDownload } from 'react-icons/fi';
import jsPDF from 'jspdf';

function SingleOrderPage() {
  const { id } = useParams();
  const [statusList, setStatusList] = useState([...orderStatusList]);
  const toast = useToast();
  const {
    single_order_loading: loading,
    single_order_error: error,
    single_order: order,
    single_order_status,
    fetchSingleOrder,
    updateOrderStatus,
  } = useOrderContext();

  const generateFacturePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(18);
    doc.setFontStyle('bold');
    doc.text('BON DE LIVRAISON', pageWidth / 2, 20, 'center');
    
    // Order info box
    doc.setFontSize(10);
    doc.setFontStyle('normal');
    const orderDate = order.createdAt ? new Date(order.createdAt).toLocaleDateString('fr-TN') : new Date().toLocaleDateString('fr-TN');
    
    // Right side box with order details
    doc.setDrawColor(0);
    doc.rect(130, 30, 70, 25);
    doc.text('N: ' + (order._id ? order._id.slice(-8).toUpperCase() : 'N/A'), 135, 38);
    doc.text('Date: ' + orderDate, 135, 45);
    doc.text('C.R: ' + (order.totalPrice || 0).toFixed(2) + ' DT', 135, 52);
    
    // Sender info (Company)
    doc.setFontStyle('bold');
    doc.text('Coordonnees Expediteur:', 15, 40);
    doc.setFontStyle('normal');
    doc.text('Compte: Art & Bois', 15, 47);
    doc.text('Adresse: Tunisie', 15, 54);
    doc.text('Tel: 54 457 134', 15, 61);
    
    // Separator line
    doc.line(15, 68, pageWidth - 15, 68);
    
    // Recipient info
    doc.setFontStyle('bold');
    doc.text('Coordonnees Destinataire:', 15, 78);
    doc.setFontStyle('normal');
    const clientName = order.user?.name || 'Client';
    const clientPhone = order.shippingInfo?.phoneNumber || 'N/A';
    const clientAddress = order.shippingInfo ? 
      ((order.shippingInfo.address || '') + ', ' + (order.shippingInfo.city || '') + ', ' + (order.shippingInfo.state || '')).replace(/^,\s*|,\s*$/g, '') : 'N/A';
    const clientGouvernorat = order.shippingInfo?.state || 'N/A';
    
    doc.text('Nom: ' + clientName, 15, 85);
    doc.text('Gouvernorat: ' + clientGouvernorat, 15, 92);
    doc.text('Adresse: ' + clientAddress, 15, 99);
    doc.text('Tel: ' + clientPhone, 15, 106);
    
    // Items table header
    const tableTop = 120;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, tableTop, pageWidth - 30, 10, 'F');
    doc.setFontStyle('bold');
    doc.text('Designation', 20, tableTop + 7);
    doc.text('P.U', 120, tableTop + 7);
    doc.text('Qte', 145, tableTop + 7);
    doc.text('Total', 170, tableTop + 7);
    
    // Items
    doc.setFontStyle('normal');
    let yPos = tableTop + 17;
    const orderItems = order.orderItems || [];
    
    orderItems.forEach((item) => {
      const itemName = item.name || 'Produit';
      const itemPrice = item.price || 0;
      const itemQty = item.quantity || 1;
      const itemTotal = itemPrice * itemQty;
      
      // Truncate long names
      const displayName = itemName.length > 35 ? itemName.substring(0, 32) + '...' : itemName;
      
      doc.text(displayName, 20, yPos);
      doc.text(itemPrice.toFixed(2), 120, yPos);
      doc.text(String(itemQty), 145, yPos);
      doc.text(itemTotal.toFixed(2), 170, yPos);
      yPos += 8;
    });
    
    // Separator line before totals
    doc.line(15, yPos + 5, pageWidth - 15, yPos + 5);
    
    // Totals
    const totalTTC = order.totalPrice || 0;
    const totalHT = (totalTTC / 1.19).toFixed(2);
    const tva = (totalTTC - parseFloat(totalHT)).toFixed(2);
    
    yPos += 15;
    doc.setFontStyle('bold');
    doc.text('Total HT:', 130, yPos);
    doc.text(totalHT + ' DT', 170, yPos);
    
    yPos += 8;
    doc.text('TVA (19%):', 130, yPos);
    doc.text(tva + ' DT', 170, yPos);
    
    yPos += 8;
    doc.setFontSize(12);
    doc.text('Total TTC:', 130, yPos);
    doc.text(totalTTC.toFixed(2) + ' DT', 170, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setFontStyle('italic');
    doc.text('Merci pour votre confiance - Art & Bois', pageWidth / 2, 280, 'center');
    
    // Download
    const fileName = clientName.replace(/\s+/g, '_') + '.pdf';
    doc.save(fileName);
    
    toast({
      position: 'top',
      description: `Facture téléchargée: ${fileName}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleChange = async (e) => {
    const status = e.target.value;
    const response = await updateOrderStatus(status, id);
    if (response.success) {
      return toast({
        position: 'top',
        description: `Order ${response.status}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchSingleOrder(id);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    let tempList = [...orderStatusList];
    if (
      single_order_status === 'processing' ||
      single_order_status === 'rejected'
    ) {
      tempList.splice(3, 2);
      setStatusList([...tempList]);
    }
    if (single_order_status === 'confirmed') {
      tempList.splice(0, 2);
      setStatusList([...tempList]);
    }
    if (single_order_status === 'shipped') {
      tempList.splice(0, 3);
      setStatusList([...tempList]);
    }
    if (single_order_status === 'delivered') {
      tempList.splice(0, 4);
      setStatusList([...tempList]);
    }
  }, [id, single_order_status]);

  if (loading) {
    return (
      <SidebarWithHeader>
        <VStack alignItems='center' justifyContent='center'>
          <Spinner size='lg' color='brown.500' />
        </VStack>
      </SidebarWithHeader>
    );
  }

  if (error) {
    return (
      <SidebarWithHeader>
        <VStack alignItems='center' justifyContent='center'>
          <Heading color='red.500'>There was an error</Heading>
        </VStack>
      </SidebarWithHeader>
    );
  }

  const isFactureEnabled = single_order_status === 'confirmed' || single_order_status === 'shipped' || single_order_status === 'delivered';

  return (
    <SidebarWithHeader>
      <HStack 
        bg='white' 
        p={{ base: 3, md: 5 }} 
        mb={5} 
        shadow='sm' 
        borderRadius='lg' 
        justifyContent='space-between'
        flexDirection={{ base: 'column', sm: 'row' }}
        gap={{ base: 3, sm: 0 }}
      >
        <HStack>
          <Text>STATUS: </Text>
          <Select
            variant='filled'
            focusBorderColor='brown.500'
            value={single_order_status}
            onChange={handleChange}
            width={{ base: '150px', md: '200px' }}
            size={{ base: 'md', md: 'lg' }}
            height={{ base: '44px', md: '48px' }}
          >
            {statusList.map((status, index) => {
              const { name, value } = status;
              return (
                <option key={index} value={value}>
                  {name}
                </option>
              );
            })}
          </Select>
        </HStack>
        <Button
          leftIcon={<FiDownload />}
          colorScheme={isFactureEnabled ? 'green' : 'gray'}
          variant='solid'
          onClick={isFactureEnabled ? generateFacturePDF : undefined}
          isDisabled={!isFactureEnabled}
          cursor={isFactureEnabled ? 'pointer' : 'not-allowed'}
          opacity={isFactureEnabled ? 1 : 0.6}
          size={{ base: 'md', md: 'lg' }}
          height={{ base: '44px', md: '48px' }}
          fontSize={{ base: 'sm', md: 'md' }}
          px={{ base: 4, md: 6 }}
          _hover={isFactureEnabled ? { bg: 'green.600' } : {}}
        >
          Télécharger Facture
        </Button>
      </HStack>
      <VStack
        spacing='5'
        p={5}
        bg='white'
        alignItems='flex-start'
        shadow='sm'
        borderRadius='lg'
      >
        <OrderDetails {...order} order_status={single_order_status} />
      </VStack>
    </SidebarWithHeader>
  );
}

export default SingleOrderPage;
